import { useState, useEffect, useMemo } from 'react';

interface UseUpstreamModuleCheckResult {
  mostRecentModuleIsUpstream: boolean;
  isCheckingModules: boolean;
  setMostRecentModuleIsUpstream: (value: boolean) => void;
  mostRecentModule: {
    type: string;
    status: string;
  } | null;
}

/**
 * Custom hook to check if the most recent module in a workflow is from an upstream step.
 * This is used to determine if downstream steps should show old results or prompt for re-processing.
 * 
 * @param workflowId - The workflow ID to check
 * @param upstreamTypes - Array of module types considered "upstream" for this step
 * @returns Object containing upstream status, loading state, and setter function
 */
export const useUpstreamModuleCheck = (
  workflowId: string | undefined,
  upstreamTypes: string[]
): UseUpstreamModuleCheckResult => {
  const [mostRecentModuleIsUpstream, setMostRecentModuleIsUpstream] = useState(false);
  const [isCheckingModules, setIsCheckingModules] = useState(true);
  const [mostRecentModule, setMostRecentModule] = useState<{ type: string; status: string } | null>(null);

  // Memoize the stringified version of upstreamTypes to avoid infinite loops
  const upstreamTypesKey = useMemo(() => JSON.stringify(upstreamTypes), [upstreamTypes.join(',')]);

  useEffect(() => {
    const checkModules = async () => {
      if (!workflowId) {
        setIsCheckingModules(false);
        return;
      }

      try {
        setIsCheckingModules(true);

        // Import workflowService
        const { workflowService } = await import('../services/api/workflowService');

        // Get workflow (includes modules in response)
        const dbWorkflow = await workflowService.getDbWorkflow(workflowId);
        const allModules = dbWorkflow.modules || [];

        // Find the most recent module (highest module_id)
        let mostRecentModule = null;
        let highestModuleId = -1;

        allModules.forEach((module: any) => {
          if (module.module_id && module.module_id > highestModuleId) {
            highestModuleId = module.module_id;
            mostRecentModule = module;
          }
        });

        // Check if most recent module is one of the upstream types
        const moduleType = mostRecentModule?.type || mostRecentModule?.module_type;
        const moduleStatus = mostRecentModule?.status || '';
        const parsedUpstreamTypes = JSON.parse(upstreamTypesKey);
        const isUpstream = moduleType && parsedUpstreamTypes.includes(moduleType);

        // Store most recent module details
        if (moduleType) {
          setMostRecentModule({ type: moduleType, status: moduleStatus });
        }

        setMostRecentModuleIsUpstream(!!isUpstream);
        console.log(`[useUpstreamModuleCheck] Most recent module: ${moduleType} | Status: ${moduleStatus} | Is upstream: ${isUpstream} | Upstream types:`, parsedUpstreamTypes);
      } catch (error) {
        console.error('[useUpstreamModuleCheck] Error checking modules:', error);
        setMostRecentModuleIsUpstream(false);
      } finally {
        setIsCheckingModules(false);
      }
    };

    checkModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, upstreamTypesKey]);

  return {
    mostRecentModuleIsUpstream,
    isCheckingModules,
    setMostRecentModuleIsUpstream,
    mostRecentModule
  };
};

